'use client';
import { useEffect, useMemo, useRef, useState } from 'react';
import { Check } from 'lucide-react';
import { twMerge } from 'tailwind-merge';
import {
  formatPollTimeLeft,
  isPollOpen,
  pollCardState,
  pollOptionTallies,
} from '@polls/message-poll';
import type { IPoll } from '@polls/service/typings';
import type { IMessage } from '@ui/components/organisms/message/message.typings';
import { useMessageContext } from '@ui/components/organisms/message/message.context';
import type { ContextUserType } from '@ui/constants/context';
import { useInteractionGuard } from '@hooks/hooks/useInteractionGuard';

export interface MessagePollProps {
  message: IMessage;
  userType?: ContextUserType;
  readOnly?: boolean;
  moderationView?: boolean;
  creatorDisplayName?: string;
  className?: string;
}

const CLOCK_TICK = 30_000;

const plural = (count: number, unit: string) => `${count} ${unit}${count === 1 ? '' : 's'}`;

const isPreviewMessage = (message: IMessage) => {
  const anyMessage = message as any;
  return (
    (typeof message.id === 'string' && message.id.startsWith('temp-')) ||
    (typeof message.slug === 'string' && message.slug.startsWith('temp-')) ||
    typeof anyMessage?.miscellaneous?.processing_status === 'string' ||
    typeof anyMessage?.processing_status === 'string'
  );
};

const usePollClock = (poll: IPoll | null | undefined) => {
  const [now, setNow] = useState(() => Date.now());
  const endDate = poll?.is_active ? poll.end_date : undefined;

  useEffect(() => {
    if (!endDate) return;
    const remaining = Date.parse(endDate) - Date.now();
    if (!Number.isFinite(remaining) || remaining <= 0) return;
    const timer = setTimeout(() => setNow(Date.now()), Math.min(remaining + 50, CLOCK_TICK));
    return () => clearTimeout(timer);
  }, [now, endDate]);

  return now;
};

const captionOf = (state: string, poll: IPoll, now: number): string | null => {
  const votes = poll.results ? plural(poll.results.total_votes, 'vote') : null;
  const ended = votes ? `Poll ended · ${votes}` : 'Poll ended';

  if (state === 'draft') return 'Voting opens when published';
  if (state === 'preview') return 'Voting opens when your post is sent';
  if (state === 'pending') return null;
  if (state === 'ended') return ended;
  if (state === 'read-only' && !isPollOpen(poll, now)) return ended;

  const left = formatPollTimeLeft(poll.end_date, now);
  const parts = [votes, left ? `ends in ${left}` : null].filter(Boolean);
  return parts.length ? parts.join(' · ') : null;
};

type TMessagePollCardProps = Omit<MessagePollProps, 'userType'> & {
  onCastPollVote?: (message: IMessage, optionId: string) => Promise<void>;
};

const MessagePollCard = ({
  message,
  onCastPollVote,
  readOnly = false,
  moderationView = false,
  creatorDisplayName,
  className,
}: TMessagePollCardProps) => {
  const poll = message.poll;
  const { interceptInteraction } = useInteractionGuard();
  const [pendingOptionId, setPendingOptionId] = useState<string | null>(null);
  const inFlight = useRef(false);
  const now = usePollClock(poll);

  const state = pollCardState({
    poll,
    platform: 'web',
    now,
    blocked: Boolean(message.block),
    moderationView,
    draft: Boolean(message.draft),
    preview: isPreviewMessage(message),
    readOnly,
    pending: pendingOptionId !== null,
  });

  const joinMessage = useMemo(() => {
    const name =
      creatorDisplayName ||
      message.community_details?.creator_display_name ||
      message.creator_display_name;
    return name
      ? 'Join community to use this feature. Collect this badge to join community!'
      : 'Join this KOR Space to use this feature. Collect this badge to join.';
  }, [creatorDisplayName, message.community_details, message.creator_display_name]);

  const castPollVote = interceptInteraction(async (event: any, optionId: string) => {
    event?.preventDefault?.();
    event?.stopPropagation?.();
    if (!onCastPollVote || inFlight.current) return;
    inFlight.current = true;
    setPendingOptionId(optionId);
    try {
      await onCastPollVote(message, optionId);
    } catch {
      // The mutation undoes its own patch and shows the failure copy.
    } finally {
      inFlight.current = false;
      setPendingOptionId(null);
    }
  }, joinMessage);

  if (!poll || state === 'none' || state === 'hidden') return null;

  const tallies = pollOptionTallies(poll);
  const hasCounts = Boolean(poll.results);
  const showBars = hasCounts && state !== 'draft' && state !== 'preview';
  const selectedId = state === 'pending' ? pendingOptionId : poll.user_selected_option?.id ?? null;
  const tappable = state === 'open' && Boolean(onCastPollVote);
  const caption = captionOf(state, poll, now);

  return (
    <div
      data-testid="message-poll"
      data-poll-state={state}
      aria-busy={state === 'pending'}
      className={twMerge(
        'flex w-full flex-col gap-2 rounded border border-primaryHover p-3',
        className,
      )}>
      <p className="font-nunito text-md font-bold" style={{ overflowWrap: 'break-word' }}>
        {poll.question}
      </p>
      <div className="flex flex-col gap-2">
        {tallies.map((tally, index) => {
          const selected = selectedId !== null && tally.id !== '' && tally.id === selectedId;
          const percentage = Math.round(tally.percentage ?? 0);
          const rowClass = twMerge(
            'relative block h-[44px] w-full overflow-hidden rounded-[12px] border text-left text-[14px]',
            selected ? 'border-primary' : 'border-primaryActive',
            tappable && 'cursor-pointer hover:bg-gray-50',
          );
          const content = (
            <>
              {showBars && (
                <span
                  aria-hidden
                  data-testid="message-poll-bar"
                  className={twMerge(
                    'absolute inset-y-0 left-0 transition-[width] duration-500',
                    selected ? 'bg-orange-200' : 'bg-gray-200',
                  )}
                  style={{ width: `${Math.min(100, Math.max(0, tally.percentage ?? 0))}%` }}
                />
              )}
              <span className="relative z-10 flex h-full items-center justify-between gap-2 px-4">
                <span className="flex min-w-0 items-center gap-2">
                  <span className="truncate font-semibold">{tally.text}</span>
                  {selected && (
                    <Check
                      role="img"
                      aria-label="Your vote"
                      className="h-4 w-4 shrink-0 text-primary"
                    />
                  )}
                </span>
                {showBars && <span className="shrink-0 text-xs font-bold">{`${percentage}%`}</span>}
              </span>
            </>
          );
          return tappable ? (
            <button
              key={`${tally.id}-${index}`}
              type="button"
              onClick={(event) => castPollVote(event, tally.id)}
              className={rowClass}>
              {content}
            </button>
          ) : (
            <div key={`${tally.id}-${index}`} className={rowClass}>
              {content}
            </div>
          );
        })}
      </div>
      <p className="min-h-[16px] text-xs text-gray-500">{caption}</p>
    </div>
  );
};

const InteractiveMessagePoll = ({ userType, ...props }: MessagePollProps) => {
  const { onCastPollVote } = useMessageContext(userType ?? 'fan');
  return <MessagePollCard {...props} onCastPollVote={onCastPollVote} />;
};

export const MessagePoll = (props: MessagePollProps) => {
  if (!props.message.poll) return null;
  if (props.readOnly || props.moderationView) return <MessagePollCard {...props} />;
  return <InteractiveMessagePoll {...props} />;
};
